import React, { useState } from 'react';
import { ArrowRight, Activity, Zap } from 'lucide-react';

// CONCEPT 2 — High-Instrumentation Command Center
const PreviewConcept2 = () => {
  const [dE, setDE] = useState(0.42);

  return (
    <main className="pt-20 pb-24 min-h-screen text-white" style={{ backgroundColor: '#0A2A63' }} data-testid="preview-c2">
      {/* Concept banner */}
      <div className="fixed top-20 right-6 z-40 bg-black/50 backdrop-blur border border-white/20 rounded-full px-4 py-2">
        <span className="text-[10px] uppercase tracking-widest font-mono text-[#FF4E50] font-bold">Preview · Concept 2</span>
      </div>

      {/* Live spectrum ticker */}
      <div className="border-y border-white/10">
        <div className="max-w-[1280px] mx-auto px-8 py-3 flex items-center gap-4">
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/60">Spectrum</span>
          <span className="font-mono text-[10px] text-cyan-300">400nm</span>
          <div className="flex-1 h-1.5 rounded-full relative overflow-hidden" style={{
            background: 'linear-gradient(90deg, #7a00ff 0%, #0044ff 15%, #00c8c8 30%, #00c800 50%, #ffee00 65%, #ff8000 80%, #ff0000 100%)'
          }}>
            <div className="absolute top-1/2 -translate-y-1/2 h-4 w-1 bg-white animate-pulse" style={{ left: '55%' }} />
          </div>
          <span className="font-mono text-[10px] text-red-300">700nm</span>
          <span className="font-mono text-[10px] uppercase tracking-widest text-emerald-300">● LIVE</span>
        </div>
      </div>

      {/* HERO — Bento */}
      <section className="max-w-[1280px] mx-auto px-8 pt-16">
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-cyan-300 mb-4">System · ProofNation ColorOS v4.2</p>
        <h1 className="text-5xl lg:text-7xl font-black tracking-tight leading-[0.98] max-w-4xl">
          Industrial color<br/>
          instrumentation<br/>
          <span className="text-[#FF4E50]">& automation.</span>
        </h1>
        <p className="mt-6 text-lg text-white/70 max-w-xl">
          Zero color deviation across every print run. Hardware, RIP and imposition — one calibration standard.
        </p>

        {/* Bento grid */}
        <div className="mt-14 grid grid-cols-12 gap-4">
          {/* Product cell */}
          <div className="col-span-12 md:col-span-6 bg-black/25 border border-white/10 rounded-2xl p-8 relative overflow-hidden">
            <div className="absolute top-4 left-4 font-mono text-[10px] uppercase tracking-widest text-cyan-300">Instrument · 01</div>
            <div className="absolute top-4 right-4 font-mono text-[10px] uppercase tracking-widest text-emerald-300 flex items-center gap-1">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" /> ONLINE
            </div>
            <img src="/nix/spectro2-lifestyle.jpg" alt="Nix Spectro 2" className="w-full h-56 object-cover rounded-xl mt-8" />
            <div className="mt-5 flex items-baseline justify-between">
              <div>
                <h3 className="text-xl font-black">Nix Spectro 2</h3>
                <p className="font-mono text-[10px] uppercase tracking-widest text-white/50 mt-1">Bluetooth 5.2 · D50 LED</p>
              </div>
              <span className="font-mono text-2xl font-black text-cyan-300">38g</span>
            </div>
          </div>

          {/* Live metrics cell */}
          <div className="col-span-12 md:col-span-6 grid grid-cols-2 gap-4">
            {[
              { k: 'ΔE2000 · avg', v: '0.42', d: 'Below G7 target (1.0)', c: 'text-emerald-300' },
              { k: 'Substrates', v: '18', d: 'Certified in library', c: 'text-white' },
              { k: 'Uptime · 30d', v: '99.87%', d: 'Across 12 press sites', c: 'text-emerald-300' },
              { k: 'Ink savings', v: '-38%', d: 'YoY vs uncalibrated', c: 'text-[#FF4E50]' },
            ].map(m => (
              <div key={m.k} className="bg-black/25 border border-white/10 rounded-2xl p-5">
                <p className="font-mono text-[10px] uppercase tracking-widest text-white/50">{m.k}</p>
                <p className={`font-mono text-3xl font-black mt-3 ${m.c}`}>{m.v}</p>
                <p className="text-[11px] text-white/60 mt-1">{m.d}</p>
              </div>
            ))}
          </div>

          {/* Interactive calibration simulator */}
          <div className="col-span-12 bg-black/25 border border-white/10 rounded-2xl p-8">
            <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-widest text-cyan-300">Signature moment</p>
                <h3 className="text-xl font-black">Interactive ΔE calibration simulator</h3>
              </div>
              <span className="font-mono text-3xl font-black" style={{ color: dE < 1 ? '#34d399' : '#FF4E50' }}>
                ΔE {dE.toFixed(2)}
              </span>
            </div>
            <input
              type="range"
              min="0" max="5" step="0.05"
              value={dE}
              onChange={(e) => setDE(parseFloat(e.target.value))}
              className="w-full accent-[#FF4E50]"
            />
            <div className="mt-5 flex gap-2">
              {[0, 0.4, 0.8, 1.2, 1.6, 2, 2.5, 3, 3.5, 4, 4.5, 5].map((step) => {
                const active = step >= dE - 0.25 && step <= dE + 0.25;
                return (
                  <div key={step} className="flex-1 h-16 rounded" style={{
                    backgroundColor: `hsl(${210 - step * 40}, ${70 - step * 5}%, ${45 + step * 3}%)`,
                    outline: active ? '2px solid #FF4E50' : 'none',
                    transform: active ? 'scale(1.08)' : 'scale(1)',
                    transition: 'all .18s ease',
                  }} />
                );
              })}
            </div>
            <p className="font-mono text-[10px] uppercase tracking-widest text-white/60 mt-4">
              Drag to see how ΔE tolerance affects perceived color match on brand-critical spot colors.
            </p>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-12 flex flex-wrap gap-4">
          <button className="bg-[#FF4E50] text-white h-12 px-8 font-bold text-sm uppercase tracking-widest hover:bg-white hover:text-[#0A2A63] transition rounded-full flex items-center gap-2">
            Calibrate system demo <ArrowRight className="h-4 w-4" />
          </button>
          <button className="border border-white/30 text-white h-12 px-8 font-bold text-sm uppercase tracking-widest hover:bg-white/10 transition rounded-full">
            View telemetry docs
          </button>
        </div>
      </section>

      {/* Partners strip on dark */}
      <section className="max-w-[1280px] mx-auto px-8 mt-24 border-t border-white/10 pt-10">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mb-6">Certified integrations</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {['barbieri','nix','printfactory','impoproof'].map(b => (
            <div key={b} className="h-20 rounded-xl border border-white/10 bg-white/5 flex items-center justify-center hover:bg-white/10 transition">
              <img src={`/logos/${b}.png`} alt={b} className="max-h-9 object-contain brightness-0 invert opacity-80" />
            </div>
          ))}
        </div>
      </section>
    </main>
  );
};

export default PreviewConcept2;
